import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { GlobalService } from '../../shared/services/global/global.service';
import { ModalsService } from '../../shared/services/modals/modals.service';
import { InvoiceService } from 'src/app/business-service/invoice/invoice.service';
import { LeadsService } from 'src/app/business-service/leads/leads.service';
import { InputValidationsService } from '../../shared/services/validations/input-validations.service';
import { ComponentReloadService } from '../../shared/services/reload/component-reload.service';
import { ResponseNotificationService } from '../../shared/services/response-notification/response-notification.service';
import { ContactsService } from 'src/app/business-service/contacts/contacts.service';
import { first } from 'rxjs';

@Component({
  selector: 'app-vendor-invoice-form',
  templateUrl: './vendor-invoice-form.component.html',
  styleUrls: ['./vendor-invoice-form.component.scss'],
})
export class VendorInvoiceFormComponent implements OnInit {

  readonly MAX_DOCUMENTS = 5;

  invoiceForm!: FormGroup;
  @Input() editInvoiceId: string | null = null;
  invoiceDetails: any;
  existingDocuments: any[] = [];

  leadDetails: any;
  vendorDetails: any;

  advanceRecieved: boolean = false;

  @Input() leadId: string | null = null;
  @Input() vendorId: string | null = null;

  amountReceviedAgainstLead: any = 0;

  currencyLov: any = [];
  defaultCurrency: any;
  vendorServices: any = [];
  constructor(
    private _modalController: ModalController,
    private _fb: FormBuilder,
    private _globalService: GlobalService,
    private _modalService: ModalsService,
    private _invoiceService: InvoiceService,
    private _leadService: LeadsService,
    private _inputValidatationService: InputValidationsService,
    private _reloadService: ComponentReloadService,
    private _responseNotificationService: ResponseNotificationService,
    private _contactsService: ContactsService
  ) { }

  async ngOnInit() {
    let organizationDetails = await this._globalService.getLoggedInOrgDetails();
    this.defaultCurrency = organizationDetails.organizationDetails.currencyCode;

    this.initializeForm();
     [this.currencyLov, this.vendorServices] = await Promise.all([
      this._globalService.loadLovData("CurrencyCode"),
      this._globalService.loadLovData("VendorServices")
    ]);

    // 👉 EDIT FLOW
    if (this.editInvoiceId) {
      await this.loadInvoiceDetails();
    }
  }

  async initializeForm() {
    this.invoiceForm = this._fb.group({
      currencyCode: [this.defaultCurrency || "USD", [Validators.required]],
      lead: [null, [Validators.required]],
      vendor: [null],
      firstName: ['', [Validators.required]],
      lastName: [''],
      phone: [null, [Validators.required]],
      email: ["", [this._inputValidatationService.emailValidator()]],
      address1: ["", []],
      organizationName: ["", [Validators.required]],
      serviceType: ["", [Validators.required]],
      invoiceDate: [null, [Validators.required]],
      dueDate: [null, [Validators.required]],
      totalAmount: ["", [Validators.required]],
      invoiceNumber: ["", [Validators.required]],
      document: [[], [Validators.required]]
    });

    if (this.leadId) {
      this.leadDetails = await this.getLeadDetails(this.leadId);
      this.invoiceForm.get('lead')?.setValue(this.leadDetails);
      this.invoiceForm.get('lead')?.disable();
    }

    if (this.vendorId) {
      this.vendorDetails = await this.getVendorDetails(this.vendorId);
      this.invoiceForm.patchValue({
        vendor: this.vendorDetails,
        firstName: this.vendorDetails.firstName || "",
        lastName: this.vendorDetails.lastName || "",
        phone: this.vendorDetails.primaryPhoneNumber || null,
        email: this.vendorDetails.email || "",
        address1: this.vendorDetails.address1 || "",
        organizationName: this.vendorDetails.organizationName || ""
      });
      this.disableVendorRealtedFields();
      this.invoiceForm.get('vendor')?.disable();
    }
  }

  async loadInvoiceDetails() {
    try {
      const res: any = await this._invoiceService.getVendorInvoiceDetails(this.editInvoiceId).toPromise();

      this.leadDetails = await this.getLeadDetails(res.leadId);
      this.vendorDetails = await this.getVendorDetails(res.vendorId);

      this.invoiceDetails = res;
      this.existingDocuments = Array.isArray(res?.documents) ? res.documents :
        (res?.documentFilePath ? [{ documentFilePath: res.documentFilePath, documentFileName: res.documentFileName || 'Invoice Document' }] : []);

      this.invoiceForm.patchValue({
        ...res,
        lead: this.leadDetails,
        vendor: this.vendorDetails,
        invoiceDate: this.convertToDate(res.invoiceDate),
        dueDate: this.convertToDate(res.dueDate),
        document: this.existingDocuments,
        address1: res.address,
      });

      // Disable lead in edit
      this.invoiceForm.get('lead')?.disable();
      this.invoiceForm.get('vendor')?.disable();
      this.disableVendorRealtedFields();

    } catch (error) {
      console.error("Error loading invoice:", error);
    }
  }

  async getLeadDetails(leadId: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this._leadService.getLead(leadId).subscribe({
        next: (response: any) => {
          resolve(response);
        },
        error: (error) => {
          console.error("Error fetching lead details:", error);
          reject(error);
        }
      });
    });
  }

  async getVendorDetails(vendorId: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this._contactsService.getVendorContact(vendorId).subscribe({
        next: (response: any) => {
          resolve(response);
        },
        error: (error) => {
          console.error("Error fetching vendor details:", error);
          reject(error);
        }
      });
    });
  }

  closeModal() {
    this._modalController.dismiss();
  }

  get todayDate(): Date {
    let date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  convertToDate(value: any): Date {
    let val = new Date(value);
    return new Date(value);
  }

  readClientDataOnSelect(data: any) {
    this.leadDetails = data;
  }

  get getCurrencyCode() {
    return this.invoiceForm.get('currencyCode')?.value;
  }

  get selectedDocuments(): any[] {
    return Array.isArray(this.invoiceForm?.get('document')?.value) ? this.invoiceForm.get('document')?.value : [];
  }

  getDocumentName(doc: any): string {
    if (doc instanceof File) {
      return doc.name || 'Uploaded document';
    }
    if(doc?.documentFileName) {
      let splits = doc.documentFileName.split('\\');
      if(splits.length > 1 && splits[2]) {
        return splits[2];
      }
    }
    return doc?.documentFileName || doc?.name || 'Document';
  }

  getDocumentUrl(file: any) {
    if (file instanceof File) {
      const fileURL = URL.createObjectURL(file);
      return fileURL;
    }

    if (typeof file === 'string') {
      return file;
    }

    return file?.documentFilePath || file?.path || '';
  }

  openImage(file: any) {
    const url = this.getDocumentUrl(file);
    if (url) {
      this._modalService.openImageModal(url);
    }
  }

  removeDocument(index: number) {
    const currentFiles = [...this.selectedDocuments];
    currentFiles.splice(index, 1);
    this.invoiceForm.get('document')?.setValue(currentFiles);
    this.invoiceForm.get('document')?.markAsTouched();
  }

  onImageChanged(event: any) {
    const incomingFiles = Array.from(event?.target?.files || []);
    if (!incomingFiles.length) {
      return;
    }

    const currentFiles = Array.isArray(this.invoiceForm.get('document')?.value) ? this.invoiceForm.get('document')?.value : [];
    const validFiles: File[] = [];

    incomingFiles.forEach((file: any) => {
      if (file.size > 5 * 1024 * 1024) {
        this._globalService.customAlert("All files size should be less than 5MB", "Invalid File Size");
        return;
      }
      validFiles.push(file);
    });

    const mergedFiles = [...currentFiles, ...validFiles].slice(0, this.MAX_DOCUMENTS);

    if (mergedFiles.length < currentFiles.length + validFiles.length) {
      this._responseNotificationService.showMessage(`You can upload a maximum of ${this.MAX_DOCUMENTS} files.`, true);
    }

    this.invoiceForm.patchValue({
      document: mergedFiles
    });
    this.invoiceForm.get('document')?.markAsTouched();

    if (event.target) {
      event.target.value = null;
    }
  }

  deleteInvoiceDocument() {
    this.invoiceForm.get('document')?.setValue([]);
    this.invoiceForm.get('document')?.markAsTouched();
  }

  readSelectedVendor(event: any) {
    const vendorId = event?.vendorId;
    if (vendorId) {
      this.getVendorDetails(vendorId).then((vendorDetails) => {
        this.invoiceForm.patchValue({
          firstName: vendorDetails.firstName || "",
          lastName: vendorDetails.lastName || "",
          phone: vendorDetails.primaryPhoneNumber || null,
          email: vendorDetails.email || "",
          address1: vendorDetails.address1 || "",
          organizationName: vendorDetails.organizationName || ""
        });
        this.disableVendorRealtedFields();

      }).catch((error) => {
        console.error("Error fetching vendor details:", error);
      });
    } else {
      this.invoiceForm.get('vendor')?.setValue(null);
    }

  }

  disableVendorRealtedFields() {
    this.invoiceForm.get('firstName')?.disable();
    this.invoiceForm.get('lastName')?.disable();
    this.invoiceForm.get('phone')?.disable();
    this.invoiceForm.get('email')?.disable();
    this.invoiceForm.get('address1')?.disable();
    this.invoiceForm.get('organizationName')?.disable();
  }

  onSubmit() {
    const hasDocument = (this.selectedDocuments?.length || 0) > 0;
    if (!hasDocument) {
      this.invoiceForm.get('document')?.setErrors({ required: true });
      this.invoiceForm.get('document')?.markAsTouched();
      this._globalService.scrollToFirstInvalidControl();
      this._globalService.customAlert("Please upload at least one invoice document.", "Invalid Form");
      return;
    }

    if (this.invoiceForm.valid) {

      const formData = this.buildFormData();

      this._invoiceService.addUpdateVendorInvoice(formData).subscribe({
        next: (res) => {
          this._responseNotificationService.showMessage(this.editInvoiceId ? "Invoice updated successfully" : "Invoice added successfully");
          this._reloadService.triggerInvoicesReload();
          this.closeModal();
        },
        error: (err) => {
          console.error("Error saving invoice:", err);
          this._globalService.customAlert("Something went wrong", "Error");
        }
      });

    } else {
      this.invoiceForm.markAllAsTouched();
      this._globalService.scrollToFirstInvalidControl();
      this._globalService.customAlert(
        "Please fill all the required fields before submitting the form.",
        "Invalid Form"
      );
    }
  }

  buildFormData(): FormData {
    const formData = new FormData();
    const formValue = this.invoiceForm.getRawValue(); // includes disabled fields
    const documentFiles = Array.isArray(formValue.document) ? formValue.document : formValue.document ? [formValue.document] : [];

    formData.append("leadId", formValue.lead?.leadId);
    formData.append("firstName", formValue.firstName);
    formData.append("lastName", formValue.lastName || "");
    formData.append("phone", JSON.stringify(formValue.phone));
    formData.append("email", formValue.email);
    formData.append("serviceType", formValue.serviceType);
    formData.append("invoiceDate", this.formatDate(formValue.invoiceDate));
    formData.append("dueDate", this.formatDate(formValue.dueDate));
    formData.append("totalAmount", formValue.totalAmount);
    formData.append("invoiceNumber", formValue.invoiceNumber);
    formData.append("currencyCode", formValue.currencyCode);
    formData.append("vendorId", formValue?.vendor?.vendorId || "");
    formData.append("address", formValue.address1);
    formData.append("organizationName", formValue.organizationName);

    documentFiles.forEach((file: any) => {
      if (file && file instanceof File) {
        formData.append("document", file);
      }
    });

    // EDIT CASE
    if (this.editInvoiceId) {
      formData.append("id", this.invoiceDetails.id);
      formData.append("invoiceId", this.invoiceDetails.invoiceId);

      const retainedDocuments = documentFiles
        .filter((doc: any) => doc && !(doc instanceof File))
        .map((doc: any, index: number) => ({
          documentFileName: doc.documentFileName,
          documentFilePath: doc.documentFilePath,
          order: index
        }));
      formData.append("documents", JSON.stringify(retainedDocuments));
    }
    return formData;
  }

  formatDate(date: any): string {
    const d = new Date(date);
    return d.toISOString(); // correct ISO format
  }

  // Function to check whether input field empty
  isFieldEmptyInForm(formControlName: string): boolean {
    return this.invoiceForm.get(formControlName)?.value === "" || this.invoiceForm.get(formControlName)?.value === null || this.invoiceForm.get(formControlName)?.value === undefined;
  }
}
